import { BaseButton } from "@/components/atom/button/BaseButton";
import { BaseNumberInput } from "@/components/atom/numberInput/BaseNumberInput";
import { BaseTextInput } from "@/components/atom/textInput/BaseInput";
import { BarcodeInput } from "@/components/molecule/barcodeInput/BarcodeInput";
import { InventoriesAutoComplete } from "@/components/organism/autoComplete/inventory/inventories-autoComplete";
import { LocationAutoComplete } from "@/components/organism/autoComplete/location/location-autoComplate";
import { COMMON_TEXT } from "@/constants/text";
import { useInputLogsFormFetch } from "@/fetch/form/useInputLogsFormFetch";
import { useInventoriesFormFetch } from "@/fetch/form/useInventoriesFormFetch";
import { useRawMaterialHandler } from "@/hooks/handler/useRawMaterialHandler";
import { useStatusHandler } from "@/hooks/handler/useStatusHandler";
import { useWorkDataStore } from "@/store/work.store";
import { koToEnKeyboard } from "@/utils/koToEnKeyboard";
import { customNotification } from "@/utils/notificationShow";
import { Styles } from "@mantine/core";
import { BaseSelectStylesNames } from "@mantine/core/lib/Select/types";
import { useForm } from "@mantine/form";
import { useEffect } from "react";
import { flushSync } from "react-dom";

const WireRodRawMaterial = Object.assign(
  {},
  {
    Button: BaseButton,
    NumberInput: BaseNumberInput,
    TextInput: BaseTextInput,
    BarcodeInput: BarcodeInput,
    InventoriesAutoComplete: InventoriesAutoComplete,
    LocationAutoComplete: LocationAutoComplete,
  }
);

const COMMON_STYLES:
  | Styles<BaseSelectStylesNames, Record<string, any>>
  | undefined = {
  label: {
    color: "white",
    fontSize: "1.0rem",
    fontWeight: 700,
  },
};

export const WireRodRawMaterialView = () => {
  const { onSubmit } = useRawMaterialHandler();
  const { handleStart, handleResume } = useStatusHandler();
  const { workData } = useWorkDataStore((state) => ({
    workData: state.workData,
  }));
  const { work } = workData;

  const {
    data: inputLogsData,
    Fetcher,
    refetch: inputLogsRefetch,
  } = useInputLogsFormFetch({
    workIds: undefined,
  });

  const inputLogs = inputLogsData?.data;

  const isLatestInputValid =
    inputLogs?.rows?.length !== 0 &&
    inputLogs?.rows?.[0].workLogType === "INPUT" &&
    inputLogs?.rows?.[0].alreadyCanceled === false;

  const fromLocationCode = work?.locationSetting?.fromLocation?.code;
  const targetLocationCode = work?.locationSetting?.toLocation?.code;
  const targetLocationName = work?.locationSetting?.toLocation?.name;

  const form = useForm({
    initialValues: {
      barcodeInput: "",
      itemCode: undefined,
      workId: work?.id,
      worksWorkIdInputPutRequest: {
        lotId: "",
        sourceLocationCode: fromLocationCode,
        targetLocationCode: undefined,
        quantity: 0,
      },
    },
  });

  const { data, refetch } = useInventoriesFormFetch({
    itemCodes: undefined,
    locationCode: fromLocationCode,
    lotName: form.values.barcodeInput,
  });

  // 작업지시 변경 시 폼 상태를 동기화하여 이전 로케이션 캐시 방지
  useEffect(() => {
    form.setValues({
      barcodeInput: "",
      itemCode: undefined,
      workId: work?.id,
      worksWorkIdInputPutRequest: {
        lotId: "",
        sourceLocationCode: fromLocationCode,
        targetLocationCode: undefined,
        quantity: 0,
      },
    });
  }, [work?.id, fromLocationCode]);

  const refetchHandler = async () => {
    const data = await refetch();
    const inventoryData = data.data.data;
    const noData = !inventoryData?.length;
    const lotId = inventoryData?.[0]?.lotId;

    if (noData) {
      form.setFieldValue("barcodeInput", "");
      form.setFieldValue("worksWorkIdInputPutRequest.lotId", "");
      form.setFieldValue("worksWorkIdInputPutRequest.quantity", 0);
      customNotification.error({
        message: COMMON_TEXT.NOTIFICATION.LOT_NOT_FOUND,
      });
    } else {
      form.setFieldValue("barcodeInput", "");
      form.setFieldValue("worksWorkIdInputPutRequest.lotId", lotId.toString());
      form.setFieldValue(
        "worksWorkIdInputPutRequest.quantity",
        Number(inventoryData[0].quantity)
      );
    }
  };

  const normalizeBarcode = (raw: string) => {
    // 필요 시 전체 대문자 정책 적용: .toUpperCase()
    return koToEnKeyboard(raw.trim());
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (!form.values.barcodeInput) return;
      const normalized = normalizeBarcode(form.values.barcodeInput);
      flushSync(() => {
        form.setFieldValue("barcodeInput", normalized);
      });
      refetchHandler();
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    if (!inputValue) return;
    flushSync(() => {
      form.setFieldValue("barcodeInput", normalizeBarcode(inputValue));
    });
    refetchHandler();
  };

  const handleOnSubmit = async () => {
    const worksWorkIdInputPutRequest = {
      lotId: Number(form.values.worksWorkIdInputPutRequest.lotId),
      sourceLocationCode: fromLocationCode,
      targetLocationCode: targetLocationCode,
      quantity: String(form.values.worksWorkIdInputPutRequest.quantity),
    };

    await onSubmit({ worksWorkIdInputPutRequest });
    await inputLogsRefetch();

    // 원부자재 투입시, 작업 상태가 WAITING 이면 START, PAUSED 면 RESUME 처리
    if (work?.trackingStatus === "WAITING") {
      await handleStart({ workId: work?.id as number });
    }
    if (work?.trackingStatus === "PAUSED") {
      await handleResume({ workId: work?.id as number });
    }
    form.reset();
  };

  return (
    <>
      <WireRodRawMaterial.LocationAutoComplete
        styles={{
          label: { color: "white", fontSize: "1.0rem", fontWeight: 700 },
        }}
        withAsterisk
        label={COMMON_TEXT.LABEL.LOCATION}
        {...form.getInputProps("worksWorkIdInputPutRequest.sourceLocationCode")}
      />
      <WireRodRawMaterial.BarcodeInput
        disabled={
          !form.values.worksWorkIdInputPutRequest.sourceLocationCode ||
          isLatestInputValid
        }
        styles={COMMON_STYLES}
        handleBlur={handleBlur}
        onKeyDown={handleKeyDown}
        {...form.getInputProps("barcodeInput")}
      />
      <WireRodRawMaterial.InventoriesAutoComplete
        disabled={
          !form.values.worksWorkIdInputPutRequest.sourceLocationCode ||
          isLatestInputValid
        }
        styles={COMMON_STYLES}
        withAsterisk
        label={COMMON_TEXT.LABEL.LOT_INFORM}
        value={form.values.worksWorkIdInputPutRequest.lotId}
        query={{
          itemCode: undefined,
          locationCode:
            form.values.worksWorkIdInputPutRequest.sourceLocationCode,
          quantity: { $gt: 0 },
        }}
        onChange={(value) => {
          const quantity = Number(
            data?.data?.filter((row) => row.lotId === Number(value))[0]
              ?.quantity ?? 0
          );
          form.setFieldValue("worksWorkIdInputPutRequest.quantity", quantity);
          form.setFieldValue("worksWorkIdInputPutRequest.lotId", value ?? "");
        }}
      />
      <WireRodRawMaterial.TextInput
        styles={COMMON_STYLES}
        withAsterisk
        disabled
        label={COMMON_TEXT.LABEL.TARGET_LOCATION_CODE}
        value={targetLocationName}
      />
      <WireRodRawMaterial.NumberInput
        disabled={isLatestInputValid}
        styles={COMMON_STYLES}
        withAsterisk
        label={COMMON_TEXT.COLUMN.QUANTITY}
        {...form.getInputProps("worksWorkIdInputPutRequest.quantity")}
      />

      <WireRodRawMaterial.Button
        onClick={handleOnSubmit}
        disabled={isLatestInputValid}
      >
        {isLatestInputValid
          ? COMMON_TEXT.BUTTON.ONLY_ONCE
          : COMMON_TEXT.BUTTON.CONFIRM}
      </WireRodRawMaterial.Button>
    </>
  );
};
